import { useState } from "react";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const navCategories = categories.slice(0, 7);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem("company_alt") as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus("success");
      setEmail("");
      return;
    }
    if (!email.trim()) return;

    setLoading(true);
    setStatus("idle");
    try {
      const formBody = new URLSearchParams();
      formBody.append("email", email.trim());
      const res = await fetch("https://readdy.ai/api/form/d917mmg4jh3v1cgkssig", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative px-4 md:px-6 pb-10 pt-4">
      <div className="max-w-[1280px] mx-auto bg-foreground-950 rounded-[28px] px-8 md:px-14 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1.5fr] gap-12 md:gap-10">
          {/* Left - Subscribe */}
          <div>
            <h3 className="text-[13px] font-label font-bold text-background-50 uppercase tracking-[0.2em] mb-4">
              Subscribe
            </h3>
            <p className="text-[13px] text-background-50/50 leading-relaxed font-body max-w-[340px] mb-6">
              Get the latest Pandabuy spreadsheet updates, new finds, and exclusive deals
              delivered straight to your inbox.
            </p>
            <form
              data-readdy-form=""
              className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3"
              onSubmit={handleSubmit}
            >
              {/* Honeypot - hidden via CSS */}
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="footer-hp-field"
              />
              <div className="flex-1 border-b border-background-50/20 pb-2">
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-transparent text-background-50 text-[14px] font-body placeholder:text-background-50/30 outline-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-50/20 text-[13px] font-label font-semibold text-background-50 hover:bg-background-50/10 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {loading ? "SENDING..." : "JOIN"}
                <i className={`${loading ? "ri-loader-4-line animate-spin" : "ri-arrow-right-line"} text-sm`}></i>
              </button>
            </form>
            {status === "success" && (
              <p className="mt-3 text-[12px] text-green-400 font-body">
                You are subscribed! Check your inbox for updates.
              </p>
            )}
            {status === "error" && (
              <p className="mt-3 text-[12px] text-red-400 font-body">
                Something went wrong. Please try again later.
              </p>
            )}
          </div>

          {/* Middle - Navigate */}
          <div>
            <h3 className="text-[13px] font-label font-bold text-background-50 uppercase tracking-[0.2em] mb-5">
              Navigate
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="text-[13px] text-background-50/55 hover:text-background-50 transition-colors duration-200 font-body underline decoration-background-50/20 hover:decoration-background-50/50 cursor-pointer"
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="#articles"
                  className="text-[13px] text-background-50/55 hover:text-background-50 transition-colors duration-200 font-body underline decoration-background-50/20 hover:decoration-background-50/50 cursor-pointer"
                >
                  Blog &amp; Guides
                </a>
              </li>
              <li>
                <a
                  href="#faq"
                  className="text-[13px] text-background-50/55 hover:text-background-50 transition-colors duration-200 font-body underline decoration-background-50/20 hover:decoration-background-50/50 cursor-pointer"
                >
                  FAQ &amp; Safety
                </a>
              </li>
              <li>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-[13px] text-background-50/55 hover:text-background-50 transition-colors duration-200 font-body underline decoration-background-50/20 hover:decoration-background-50/50 cursor-pointer"
                >
                  Pandabuy Sheet
                </a>
              </li>
            </ul>
          </div>

          {/* Right - Contact */}
          <div>
            <h3 className="text-[13px] font-label font-bold text-background-50 uppercase tracking-[0.2em] mb-5">
              Contact
            </h3>
            <div className="space-y-2 mb-8">
              <p className="text-[13px] text-background-50/50 font-body">
                <i className="ri-mail-line mr-2 text-background-50/40"></i>
                hello@pandabuy.fit
              </p>
            </div>

            <h3 className="text-[13px] font-label font-bold text-background-50 uppercase tracking-[0.2em] mb-5">
              Address
            </h3>
            <p className="text-[13px] text-background-50/40 leading-relaxed font-body">
              123 Fashion District,
              <br />
              Los Angeles, CA 90013
              <br />
              United States
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-14 pt-8 border-t border-background-50/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <a
              href="#"
              className="text-[12px] text-background-50/40 hover:text-background-50/70 transition-colors duration-200 font-body underline decoration-background-50/15 cursor-pointer"
            >
              Instagram
            </a>
            <a
              href="#"
              className="text-[12px] text-background-50/40 hover:text-background-50/70 transition-colors duration-200 font-body underline decoration-background-50/15 cursor-pointer"
            >
              Twitter
            </a>
            <a
              href="#"
              className="text-[12px] text-background-50/40 hover:text-background-50/70 transition-colors duration-200 font-body underline decoration-background-50/15 cursor-pointer"
            >
              TikTok
            </a>
          </div>

          <p className="text-[12px] text-background-50/30 font-body">
            &copy; {new Date().getFullYear()} Pandabuy.fit — All rights reserved.
          </p>

          <div className="flex items-center gap-5">
            <a
              href="#"
              className="text-[12px] text-background-50/40 hover:text-background-50/70 transition-colors duration-200 font-body underline decoration-background-50/15 cursor-pointer"
            >
              Privacy Policy
            </a>
            <a
              href="#"
              className="text-[12px] text-background-50/40 hover:text-background-50/70 transition-colors duration-200 font-body underline decoration-background-50/15 cursor-pointer"
            >
              Terms
            </a>
          </div>
        </div>
      </div>

      {/* Popular Categories in Footer */}
      <div className="max-w-[1280px] mx-auto mt-8 px-4">
        <p className="text-[11px] font-label font-semibold text-foreground-400 uppercase tracking-wider mb-3">
          Popular Pandabuy Spreadsheet Categories
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {navCategories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="text-[12px] text-foreground-400 hover:text-foreground-700 transition-colors duration-200 font-body cursor-pointer"
            >
              {cat.name} Spreadsheet
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}